import React, { useEffect, useRef, useState } from "react";
import { ArrowLeftIcon, CalendarIcon, UserIcon } from "@heroicons/react/24/outline";
import SlideOver from "src/components/shared/SlideOver";
import FormResponse from "./FormResponse";
import Preloader from "src/components/shared/LoadingAnimations/Preloader";

export default function AllResponses({ formResponses, onBack, loading = false }) {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [formResponse, setFormResponse] = useState(null);

  const slideOverRef = useRef(null);

  const handleOpen = (response) => {
    setFormResponse(response);
    setOpenDrawer(true);
  };

  const handleClose = () => {
    setOpenDrawer(false);
    setFormResponse(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (openDrawer) {
      const timer = setTimeout(() => {
        if (slideOverRef.current) {
          slideOverRef.current.scrollTo({ top: 0, behavior: "instant" });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [openDrawer]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200">
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Form
          </button>
        </div>

        <div className="rounded-lg bg-white p-8 text-center shadow">
          <Preloader text="Loading responses..." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200">
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Form
          </button>
        </div>
        <div className="text-sm text-gray-600">{formResponses?.length || 0} submission(s) found</div>
      </div>

      <div className="rounded-lg border-l-4 border-highlightColor bg-white p-6 shadow">
        <h1 className="mb-2 text-2xl font-bold text-gray-800">Previous Submissions</h1>
        <p className="text-gray-600">View all your previous responses to this form</p>
      </div>

      {!formResponses || formResponses.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center shadow">
          <div className="mb-4 text-gray-400">
            <UserIcon className="mx-auto h-12 w-12" />
          </div>
          <h3 className="mb-2 text-lg font-medium text-gray-800">No Previous Submissions</h3>
          <p className="text-gray-600">You haven't submitted any responses to this form yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {formResponses.map((response, index) => (
            <div
              key={response._id || index}
              onClick={() => handleOpen(response)}
              className="cursor-pointer rounded-lg bg-white p-6 shadow transition-shadow hover:shadow-md">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600 hover:underline">
                    <CalendarIcon className="h-4 w-4" />
                    Submitted: {formatDate(response.created_at)}
                  </div>
                </div>
                <div className="text-sm font-medium text-highlightColor">Submission #{formResponses?.length - index}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <SlideOver
        size="4xl"
        isOpen={openDrawer}
        handleClose={handleClose}
        hideHeader={true}
        onOpenChange={setOpenDrawer}>
        <div
          ref={slideOverRef}
          className="h-full overflow-y-auto">
          {formResponse && (
            <FormResponse
              hideSubmission
              response={formResponse}
            />
          )}
        </div>
      </SlideOver>
    </div>
  );
}
